import React from 'react';
import { makeStyles } from 'tss-react/mui';
import { useAppSelector } from 'hooks/redux';

import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import LinkIcon from '@mui/icons-material/Link';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ButtonBase from '@mui/material/ButtonBase';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';

import { scheduledTransactionsUtils } from 'app-components/flux/scheduled-transactions';
import {
  AutoAdjustAmountEnum,
  type ScheduledTransactionType,
} from 'app-components/flux/scheduled-transactions/scheduledTransactionsTypes';
import { billPresentmentSelectors } from 'app-components/flux/bill-presentment';

import type { ScheduledTransactionEditFormAccessors } from '../types';

const useStyles = makeStyles()((theme) => ({
  button: {
    maxWidth: 5150,
    minWidth: 200,
    boxShadow: theme.shadows[3],
    transition: '300ms',
    background: theme.palette.background.paper,
    border: `1px solid ${theme.palette.border.light}`,
    borderRadius: 12,
    padding: 16,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    '&:hover': {
      cursor: 'pointer',
      boxShadow: theme.shadows[5],
      background: theme.palette.action.hover,
      backgroundColor: theme.palette.action.hover,
    },
  },
  iconColor: {
    color: theme.palette.primary.main,
    marginBottom: 'auto',
  },
  textSummary: {
    flex: 1,
    width: '100%',
    marginTop: 8,
    padding: 16,
    verticalAlign: 'middle',
    borderRadius: 12,
    backgroundColor: theme.palette.info.light,
    color: theme.palette.info.dark,
  },
  bold: {
    fontWeight: 500,
  },
}));

interface BillConnectButtonProps {
  onClick: () => void;
  form: ScheduledTransactionEditFormAccessors;
}

const BillConnectButton = (props: BillConnectButtonProps) => {
  const { onClick, form } = props;
  const { classes } = useStyles();

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (onClick && (e.key === 'Enter' || e.keyCode === 13)) onClick();
  };

  const { autoAdjustAmount, autoAdjustDueOn } = form.values || {};

  const allDisabled = Boolean(
    (!autoAdjustAmount || autoAdjustAmount === AutoAdjustAmountEnum.none) && !autoAdjustDueOn,
  );

  const leftSide = scheduledTransactionsUtils.getDisplayNameFromAdjustAmount(autoAdjustAmount);

  const hasBVA = useAppSelector((state) =>
    billPresentmentSelectors.getHasBVA(state, form.values as unknown as ScheduledTransactionType),
  );

  const connected = Boolean(form.values?.presentmentAccountId || hasBVA);

  return (
    <ButtonBase
      id='BillConnectButton'
      aria-label='bill-connnect-button'
      data-testid='BillConnectButton'
      component={Paper}
      elevation={4}
      onKeyDown={handleKeyDown}
      onClick={onClick}
      className={classes.button}
    >
      <Box display='flex' flexDirection='row' justifyContent='space-between' alignItems='center' width='100%' gap={2}>
        <Box display='flex' flexDirection='column' gap={1}>
          <Box display='flex' flexDirection='row' gap={1}>
            <LinkIcon className={classes.iconColor} />
            <Typography variant='bodyMedium'>Bill Connect</Typography>
          </Box>

          <Box display='flex' flexDirection='row' gap={1}>
            <Typography variant='paragraphRegular' color='textSecondary'>
              {'Once connected, '}
              <Typography component='span' variant='paragraphMedium' color='text.primary'>
                Quicken can pull fluctuating amounts & dates directly
              </Typography>
              {' so your Reminders stay up to date seamlessly.'}
            </Typography>
          </Box>
        </Box>

        <Box>
          <Button variant='text' endIcon={<ArrowForwardRoundedIcon />}>
            <Typography component='span' variant='paragraphMedium'>
              {connected ? 'Change' : 'Connect'}
            </Typography>
          </Button>
        </Box>
      </Box>
      {connected && !allDisabled ? (
        <Typography variant='captionRegular' className={classes.textSummary}>
          {'Display my '}
          <span className={classes.bold}>{leftSide}</span>
          {leftSide && autoAdjustDueOn ? ' and ' : null}
          {autoAdjustDueOn ? <span className={classes.bold}>Due Date</span> : null}
          {' once the statement period closes.'}
        </Typography>
      ) : null}
    </ButtonBase>
  );
};

BillConnectButton.displayName = 'BillConnectButton';

export default BillConnectButton;
